<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  CARD_SEO_DATA,
  MINOR_ARCANA_SLUGS,
  SUIT_LABELS,
  SUIT_ORDER,
  SUIT_PAPER,
  cardImageSrc,
  type CardSeoData,
  type CardSuit,
} from '@app/data/cardSeoData';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import CardFan from '@app/presentation/components/paper/CardFan.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoCardSticker from '@app/presentation/components/seo/SeoCardSticker.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const route = useRoute();
const router = useRouter();

const suitParam = route.params.suit as string;
const isValidSuit = (SUIT_ORDER as string[]).includes(suitParam);

if (!isValidSuit) {
  router.replace('/karty/masti');
}

const suit = suitParam as CardSuit;

// Per-suit content: element + authored on-brand longread (all 4 suits). The patch
// paper (stock + fabric) is shared with /karty/masti via SUIT_PAPER.
interface SuitContent {
  element: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longread: string[];
  inLove: string;
  inWork: string;
  inEveryday: string;
}

const SUIT_CONTENT: Record<CardSuit, SuitContent> = {
  kubki: {
    element: 'Вода',
    metaTitle: 'Масть Кубков в Таро — Значение, Стихия Вода | Таро Мяу',
    metaDescription:
      'Масть Кубков (вода) в Таро отвечает за чувства, любовь и отношения. Разбор стихии, тем масти и все 14 карт Кубков — от Туза до Короля.',
    intro:
      'Кубки — это масть воды и сердца. Всё, что мы чувствуем, любим и переживаем внутри, живёт именно здесь: нежность и обида, влюблённость и разочарование, дружба и одиночество.',
    longread: [
      'Стихия Кубков — вода. Вода принимает форму сосуда, течёт, наполняется и переливается через край — так же ведут себя и наши чувства. Поэтому масть Кубков говорит не о делах и не о фактах, а о том, что между людьми происходит на уровне сердца: симпатия, привязанность, прощение, тоска.',
      'В колоде Кубки разворачивают целую историю чувства — от первого тёплого импульса Туза, через союз двоих и радость встреч, к разочарованию, ностальгии и, наконец, к спокойной семейной полноте. Это путь сердца, которое учится открываться, терять и снова доверять.',
      'Перевёрнутые или «тяжёлые» Кубки — это не наказание, а сигнал о переполнении: где-то слишком много эмоций, иллюзий или зависимости от чужого тепла. Масть мягко зовёт навести порядок в чувствах, а не глушить их.',
    ],
    inLove:
      'Прямая территория масти: чувства, близость, готовность открыться и быть уязвимым. Кубки показывают, насколько в отношениях тепло и искренне.',
    inWork:
      'В делах Кубки говорят про атмосферу, отношения в команде и про то, любишь ли ты то, чем занят, — а не про деньги и KPI.',
    inEveryday:
      'В быту это про настроение, дружбу, заботу о близких и о собственном эмоциональном ресурсе.',
  },
  pentakli: {
    element: 'Земля',
    metaTitle: 'Масть Пентаклей в Таро — Значение, Стихия Земля | Таро Мяу',
    metaDescription:
      'Масть Пентаклей (земля) в Таро отвечает за деньги, работу, тело и всё материальное. Разбор стихии, тем масти и все 14 карт Пентаклей.',
    intro:
      'Пентакли — это масть земли и материи. Деньги, работа, дом, здоровье тела, всё, что можно потрогать и измерить, относится сюда. Это самая «приземлённая» и устойчивая масть колоды.',
    longread: [
      'Земля медленная, надёжная, плодородная: она не обещает мгновенного результата, но вознаграждает за труд и терпение — и именно её стихию несёт масть Пентаклей. Это карты про ресурсы и про то, как мы их выращиваем: деньги, навыки, имущество, тело, ощущение безопасности.',
      'В колоде Пентакли проходят путь от первого зерна-возможности Туза, через освоение ремесла и кропотливый труд, через бедность и страх нехватки — к стабильному достатку, наследию и спокойной зрелой опоре. Это история о том, как из малого вырастает прочное.',
      'Перевёрнутые Пентакли обычно говорят о потере опоры: жадности или, наоборот, расточительстве, переработках в ущерб себе, страхе бедности или застрявших, не доведённых до результата делах. Масть напоминает, что материальное — фундамент, но не смысл.',
    ],
    inLove:
      'В чувствах Пентакли — про надёжность, верность и быт вдвоём: про отношения, на которые можно опереться, а не про вспышку страсти.',
    inWork:
      'Главная территория масти: работа, деньги, карьера, проекты и всё, что приносит ощутимый результат и стабильный доход.',
    inEveryday:
      'В быту это про здоровье тела, дом, режим, ремонт, покупки и спокойную почву под ногами.',
  },
  mechi: {
    element: 'Воздух',
    metaTitle: 'Масть Мечей в Таро — Значение, Стихия Воздух | Таро Мяу',
    metaDescription:
      'Масть Мечей (воздух) в Таро отвечает за мысли, слова, правду и конфликты. Разбор стихии, тем масти и все 14 карт Мечей.',
    intro:
      'Мечи — это масть воздуха и ума. Мысли, слова, решения, споры и честная правда живут здесь. Это самая острая масть колоды: она режет иллюзии, но может ранить и саму себя.',
    longread: [
      'Острый ум разрубает узлы — но тот же клинок ранит: таков характер масти Мечей с её стихией воздуха. Это ясность, логика и движение мысли, но также ветер тревоги и буря конфликта. Мечи говорят о том, что происходит в голове: какие решения мы принимаем, что говорим вслух, о чём тревожимся и где спорим.',
      'В колоде Мечи проходят, пожалуй, самый драматичный путь: от ясного прорыва Туза и трудного выбора, через боль, бессонные ночи и предательство, к моменту, когда буря отступает и наступает облегчение. Это история ума, который учится отличать реальную угрозу от выдуманной.',
      'Перевёрнутые Мечи часто указывают на накрученные мысли, недосказанность, манипуляции или усталость от внутренней войны. Масть напоминает: меч хорош, когда отсекает лишнее, а не когда мы раним им себя и близких.',
    ],
    inLove:
      'В чувствах Мечи — про разговоры, честность и конфликты: про то, что сказано и не сказано, про обиды и про необходимость прояснить отношения словами.',
    inWork:
      'В делах Мечи — про стратегию, переговоры, решения и конкуренцию, а иногда про стресс, дедлайны и столкновение интересов.',
    inEveryday:
      'В быту это про тревожные мысли, важные разговоры, договорённости и умение называть вещи своими именами.',
  },
  zhezly: {
    element: 'Огонь',
    metaTitle: 'Масть Жезлов в Таро — Значение, Стихия Огонь | Таро Мяу',
    metaDescription:
      'Масть Жезлов (огонь) в Таро отвечает за действие, страсть, энергию и волю. Разбор стихии, тем масти и все 14 карт Жезлов.',
    intro:
      'Жезлы — это масть огня и воли. Энергия, страсть, замыслы, действие и движение вперёд живут здесь. Это самая горячая и подвижная масть колоды — про азарт жить и творить.',
    longread: [
      'Стихия Жезлов — огонь. Огонь зажигает, греет, толкает вперёд, но требует топлива и может выжечь. Поэтому масть Жезлов — про вдохновение и инициативу: про желание начать, рискнуть, заявить о себе и довести задуманное до результата.',
      'В колоде Жезлы проходят путь предпринимателя и творца: от искры-идеи Туза, через первые победы и азарт соперничества, через усталость и перегруз от взятого на себя — к зрелой, уверенной силе вести за собой. Это история воли, которая учится не только вспыхивать, но и гореть ровно.',
      'Перевёрнутые Жезлы обычно говорят о выгорании, прокрастинации, разбросанности или конфликте амбиций. Масть напоминает: страсть — отличное топливо, но её нужно направлять, а не давать гореть впустую.',
    ],
    inLove:
      'В чувствах Жезлы — про страсть, влечение и азарт, про яркое притяжение и инициативу, а иногда про ревность и борьбу характеров.',
    inWork:
      'В делах Жезлы — про амбиции, запуск проектов, творчество, лидерство и смелые шаги вперёд.',
    inEveryday:
      'В быту это про энергию, спорт, путешествия, новые начинания и желание что-то менять.',
  },
};

const content = computed<SuitContent | null>(() => (isValidSuit ? SUIT_CONTENT[suit] : null));
const label = computed(() => (isValidSuit ? SUIT_LABELS[suit] : ''));
// «масть Кубков», not «масть Кубки» — the headings put the suit after «масть» (as the meta titles do).
const SUIT_GENITIVE: Record<CardSuit, string> = {
  kubki: 'Кубков',
  pentakli: 'Пентаклей',
  mechi: 'Мечей',
  zhezly: 'Жезлов',
};
const labelGen = computed(() => (isValidSuit ? SUIT_GENITIVE[suit] : ''));

// 14 cards of this suit, in canonical minor-arcana order.
const suitCards = computed<CardSeoData[]>(() => {
  if (!isValidSuit) return [];
  return MINOR_ARCANA_SLUGS.map((s) => CARD_SEO_DATA[s])
    .filter((c): c is CardSeoData => Boolean(c) && c.suit === suit);
});

// Other suits for cross-linking at the bottom.
const otherSuits = computed(() =>
  SUIT_ORDER.filter((s) => s !== suit).map((s) => ({
    suit: s,
    label: SUIT_LABELS[s],
    element: SUIT_CONTENT[s].element,
    paper: SUIT_PAPER[s],
  })),
);

// This suit's patch paper, and its Ace, Queen and King face up for the hero fan.
const paper = isValidSuit ? SUIT_PAPER[suit] : { stock: '', fabric: '' };
const heroFaces = computed(() =>
  suitCards.value.filter((_, i) => i === 0 || i >= 12).map((c) => cardImageSrc(c.slug).webp),
);

if (isValidSuit) {
  const c = SUIT_CONTENT[suit];
  useHead({
    title: c.metaTitle,
    meta: [
      { name: 'description', content: c.metaDescription },
      { name: 'keywords', content: `${SUIT_LABELS[suit]} таро, масть ${SUIT_LABELS[suit].toLowerCase()}, ${SUIT_LABELS[suit].toLowerCase()} таро значение, стихия ${c.element.toLowerCase()} таро` },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: c.metaTitle },
      { property: 'og:description', content: c.metaDescription },
      { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
      { property: 'og:locale', content: 'ru_RU' },
      { property: 'og:site_name', content: 'Таро Мяу' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: c.metaTitle },
      { name: 'twitter:description', content: c.metaDescription },
      { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    ],
    link: [
      { rel: 'canonical', href: `https://tarotmeow.ru/karty/masti/${suit}` },
      { rel: 'alternate', hreflang: 'ru', href: `https://tarotmeow.ru/karty/masti/${suit}` },
      { rel: 'alternate', hreflang: 'x-default', href: `https://tarotmeow.ru/karty/masti/${suit}` },
    ],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
            { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
            { '@type': 'ListItem', position: 3, name: 'Масти', item: 'https://tarotmeow.ru/karty/masti' },
            { '@type': 'ListItem', position: 4, name: SUIT_LABELS[suit], item: `https://tarotmeow.ru/karty/masti/${suit}` },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `Карты масти ${SUIT_GENITIVE[suit]}`,
          numberOfItems: 14,
          itemListElement: MINOR_ARCANA_SLUGS.map((s) => CARD_SEO_DATA[s])
            .filter((card): card is CardSeoData => Boolean(card) && card.suit === suit)
            .map((card, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: card.name,
              url: `https://tarotmeow.ru/karty/${card.slug}`,
            })),
        }),
      },
    ],
  });
}
</script>

<template>
  <article
    v-if="content"
    class="pb-4"
  >
    <!-- ============================ HERO ============================ -->
    <!-- A patch of the suit's own fabric, its Ace, Queen and King fanned into the corner. -->
    <section class="pt-6 pb-14 md:pt-10 md:pb-20">
      <div class="site-container">
        <SeoCrumbs
          :items="[
            { label: 'Главная', to: '/' },
            { label: 'Карты', to: '/karty' },
            { label: 'Масти', to: '/karty/masti' },
            { label },
          ]"
        />

        <div
          class="paper-sheet relative flex flex-col overflow-hidden px-7 pt-9 text-foreground md:flex-row md:items-end md:gap-10 md:px-12 md:py-12"
          :class="[paper.stock, paper.fabric]"
        >
          <div class="relative z-[1] max-w-[760px] flex-1 space-y-5">
            <div class="flex flex-wrap items-center gap-2.5">
              <span
                class="seo-tag paper-tag type-overline px-2.5 py-0.5"
                style="--tilt: -1.5deg"
              >
                Стихия: {{ content.element }}
              </span>
              <span
                class="seo-tag paper-tag type-overline px-2.5 py-0.5"
                style="--tilt: 1deg"
              >
                14 карт · младший аркан
              </span>
            </div>
            <h1 class="type-display">Масть {{ labelGen }}</h1>
            <p class="type-body max-w-[680px] md:text-[17px]">{{ content.intro }}</p>
          </div>
          <div
            class="relative z-[1] -mb-6 mt-6 self-end md:-mb-20 md:mt-0"
            aria-hidden="true"
          >
            <CardFan
              :faces="heroFaces"
              :card-width="64"
              :spread="12"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ====================== ЛОНГРИД О МАСТИ ====================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Стихия и темы</div>
          <h2 class="type-title">О чём говорит масть {{ labelGen }}</h2>
        </ScrollReveal>
        <div class="max-w-[760px] space-y-5">
          <ScrollReveal
            v-for="(para, i) in content.longread"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <p class="type-body text-muted-foreground md:text-[17px] md:leading-[1.75]">{{ para }}</p>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- ====================== 14 КАРТ МАСТИ ====================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8 flex items-end justify-between gap-4">
          <div>
            <div class="type-overline mb-3.5 text-primary-ink">Все карты</div>
            <h2 class="type-title">14 карт масти {{ labelGen }}</h2>
          </div>
          <RouterLink
            to="/karty"
            class="seo-more type-label hidden shrink-0 sm:block"
          >
            Вся колода →
          </RouterLink>
        </ScrollReveal>

        <ul class="grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 lg:gap-x-6 lg:gap-y-8">
          <li
            v-for="(card, i) in suitCards"
            :key="card.slug"
          >
            <SeoCardSticker
              :slug="card.slug"
              :name="card.name"
              :number="card.number"
              :index="i"
            />
          </li>
        </ul>
      </div>
    </section>

    <!-- ====================== МАСТЬ В ЛЮБВИ / В РАБОТЕ ====================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Коротко по сферам</div>
          <h2 class="type-title">Масть {{ labelGen }} в жизни</h2>
        </ScrollReveal>

        <ScrollReveal class="paper-sheet max-w-[820px] px-6">
          <dl class="seo-lines">
            <div class="flex flex-col gap-1.5 py-5 sm:flex-row sm:gap-6">
              <dt class="type-app-heading text-foreground sm:w-40 sm:shrink-0">В любви</dt>
              <dd class="type-body text-muted-foreground">{{ content.inLove }}</dd>
            </div>
            <div class="flex flex-col gap-1.5 py-5 sm:flex-row sm:gap-6">
              <dt class="type-app-heading text-foreground sm:w-40 sm:shrink-0">В работе</dt>
              <dd class="type-body text-muted-foreground">{{ content.inWork }}</dd>
            </div>
            <div class="flex flex-col gap-1.5 py-5 sm:flex-row sm:gap-6">
              <dt class="type-app-heading text-foreground sm:w-40 sm:shrink-0">В быту</dt>
              <dd class="type-body text-muted-foreground">{{ content.inEveryday }}</dd>
            </div>
          </dl>
        </ScrollReveal>
      </div>
    </section>

    <!-- ====================== ДРУГИЕ МАСТИ ====================== -->
    <section class="pb-14 md:pb-20">
      <div class="site-container">
        <ScrollReveal class="mb-8">
          <div class="type-overline mb-3.5 text-primary-ink">Не останавливайся</div>
          <h2 class="type-title">Другие масти</h2>
        </ScrollReveal>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <RouterLink
            v-for="(o, i) in otherSuits"
            :key="o.suit"
            :to="`/karty/masti/${o.suit}`"
            class="sh-patch paper-sheet relative flex min-h-[128px] flex-col justify-between overflow-hidden p-6 text-foreground"
            :class="[o.paper.stock, o.paper.fabric]"
          >
            <span
              class="seo-tag paper-tag type-overline relative z-[1] w-fit px-2 py-0.5"
              :style="{ '--tilt': `${i % 2 ? 1.2 : -1.5}deg` }"
            >{{ o.element }}</span>
            <div class="relative z-[1] pt-4">
              <h3 class="type-subheading">{{ o.label }}</h3>
              <span class="type-label mt-1 inline-block">Открыть →</span>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- ========================= BOTTOM CTA ======================== -->
    <div class="site-container py-6 md:py-10">
      <ScrollReveal>
        <SeoFinalCta>
          <h2 class="type-title">Выпала карта масти {{ labelGen }}?</h2>
          <p class="type-body mx-auto mb-8 mt-[18px] max-w-[560px] text-muted-foreground md:text-lg">
            Собери расклад — кот объяснит, что значит эта масть именно в твоей ситуации. Первый расклад
            бесплатно.
          </p>
          <div class="flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/karty/masti">Все масти</RouterLink>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </article>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
.sh-patch {
  transition: translate 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}
@media (hover: hover) {
  .sh-patch:hover {
    translate: 0 -3px;
  }
}
.sh-patch:active {
  translate: 0 1px;
}
.sh-patch:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .sh-patch {
    transition: none;
  }
}
</style>
